import NotFound from "components/not-found/NotFound";
import useFullPageLoader from "hooks/useFullPageLoader";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Pagination from "react-js-pagination";
import { useSearchParams } from "react-router-dom";
import { Button } from "components/Button/Button";
import useMuiButtonLoader from "hooks/useMuiButtonLoader";
import { Table } from "react-bootstrap";
import FormControl from '@mui/material/FormControl';
import {
  downloadLectureReport,
  getLectureReport,
  getOnDemandLectureReport,
} from "services/reports/reportController";
import ReactDatePicker from "react-datepicker";
import {
  convertMinutesToHoursMinutesSeconds,
  dateFormat,
  dateipFormat,
  simpleDateFormat,
} from "utils";
import * as XLSX from "xlsx";

const OnDemandLectureReport = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  let activePage = +searchParam.get("page") || 1;

  const [record, setRecord] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(+searchParam.get("limit") || 10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [search, setSearch] = useState(searchParam.get("query") || "");

  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [submitButtonElement, setSubmitLoading] = useMuiButtonLoader();
  const [submitButtonElement2, setSubmitLoading2] = useMuiButtonLoader();

  // API call function
  const reportListing = async (
    page = activePage,
    pageLimit = limit,
    override = null
  ) => {
    try {
      showLoader();

      const payload = {
        page,
        limit: pageLimit,
        search: override?.search ?? search,
        isDownload: 0,
      };

      if (override?.fromDate && override?.toDate) {
        payload.fromDate = override.fromDate;
        payload.toDate = override.toDate;
      } else if (fromDate && toDate) {
        payload.fromDate = simpleDateFormat(fromDate);
        payload.toDate = simpleDateFormat(toDate);
      }

      const res = await getOnDemandLectureReport(payload);

      hideLoader();
      if (res.code === 200) {
        setRecord(res.data.list || []);
        setTotal(res.data.count || 0);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      hideLoader();
      toast.error(err?.response?.data?.message || err.message);
    }
  };


  // ─────────────────────────────────────────────
  // SEARCH BUTTON PRESS
  // ─────────────────────────────────────────────
  const handleSearch = () => {
    searchParam.set("page", 1);
    searchParam.set("query", search || "");
    setSearchParam(searchParam);

    reportListing(1, limit);
  };

  // ─────────────────────────────────────────────
  // RESET FILTER
  // ─────────────────────────────────────────────
  const resetFilter = () => {
    // Clear UI fields
    setSearch("");
    setFromDate("");
    setToDate("");

    // Update URL
    searchParam.delete("query");
    searchParam.delete("page");
    setSearchParam(searchParam);

    // Call API using hard-reset values (not using state which updates async)
    reportListing(1, limit, {
      search: "",
      fromDate: "",
      toDate: "",
    });
  };


  // ─────────────────────────────────────────────
  // PAGINATION
  // ─────────────────────────────────────────────
  const handlePageChange = (page) => {
    searchParam.set("page", page);
    setSearchParam(searchParam);

    reportListing(page, limit);
  };

  // ─────────────────────────────────────────────
  // LIMIT CHANGE
  // ─────────────────────────────────────────────
  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);

    searchParam.set("limit", newLimit);
    searchParam.set("page", 1);
    setSearchParam(searchParam);

    reportListing(1, newLimit);
  };

  // Initial fetch (mount only)
  useEffect(() => {
    reportListing(activePage, limit);
  }, []);

  // ─────────────────────────────────────────────
  // DOWNLOAD EXCEL
  // ─────────────────────────────────────────────
  const downloadHandler = async () => {
    console.log("🔽 DOWNLOAD HANDLER TRIGGERED");

    try {
      setSubmitLoading2(true);

      const payload = {
        search: search || "",
        isDownload: 1,
        limit: limit,
        page: activePage,
      };

      if (fromDate && toDate) {
        payload.fromDate = simpleDateFormat(fromDate);
        payload.toDate = simpleDateFormat(toDate);
      }


      // API CALL
      const res = await getOnDemandLectureReport(payload);

      const lectureData = res?.data?.list;

      if (res.code !== 200) {
        toast.error(res.message || "API Error");
        return;
      }

      if (!Array.isArray(lectureData)) {
        toast.error("Invalid data format from server");
        return;
      }

      if (lectureData.length === 0) {
        console.log("⚠ No data available for export");
        toast.error("No data available for export");
        return;
      }

      const data = lectureData.map((item, i) => ({
        "S.No": i + 1,
        "Name": `${item.user.FirstName || ""} ${item.user.MiddleName || ""} ${item.user.LastName || ""}`.trim(),
        "Email": item.user?.EmailID || "",
        "Mobile": item.user?.Mobile || "",
        "Course": item.course?.CourseTitle || "",
        "Lecture Title": item.lecture?.LectureTitle || "",
        "Played Time": convertMinutesToHoursMinutesSeconds(item.TotalPlayedTime),
        "Platform": item.Platform,
        "IP Address": item.IPAddress,
        "Start Time": dateipFormat(item.AddedOn),
        "End Time": dateipFormat(item.EndTime),
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "LectureLog");

      XLSX.writeFile(workbook, "LectureLogReport.xlsx");


    } catch (err) {
      toast.error("Error downloading Excel file");
    } finally {
      console.log("⏹ Loader stopped");
      setSubmitLoading2(false);
    }
  };



  return (
    <React.Fragment>
      <title>Shubhra Viraj | OnDemand Lecture Report</title>
      <div className="">
        <div className="WrapperBox">
          <div className="Small-Wrapper">

            {/* SEARCH BAR */}
            <div className="search_wrap">
              <div className="SearchBox">
                <div className="div_wrap mx-3">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="div_wrap w-25">
                  <ReactDatePicker
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    className="form-control"
                    showIcon
                    dateFormat="dd-MMM-yyyy"
                    placeholderText="dd-MMM-yyyy"
                    showMonthDropdown
                    showYearDropdown
                  />
                </div>

                <div className="div_wrap w-25">
                  <ReactDatePicker
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    className="form-control"
                    showIcon
                    dateFormat="dd-MMM-yyyy"
                    placeholderText="dd-MMM-yyyy"
                    showMonthDropdown
                    showYearDropdown
                  />
                </div>

                <Button onClick={handleSearch} {...submitButtonElement}>Search</Button>

                <Button className="refresh_btnn mx-2" onClick={resetFilter}>
                  <i className="fa fa-refresh"></i>
                </Button>
              </div>

              <h4 className="Title">OnDemand Lecture Report</h4>
            </div>

            {/* ROWS + EXPORT */}
            <div className="search_wrap">
              <div className="SearchBox">
                <div className="div_wrap2">
                  <label className="form-label-dp">No of rows:</label>
                  <FormControl sx={{ m: 1, minWidth: 40 }} size="small">
                    <select
                      className="form-control-dp"
                      value={limit}
                      onChange={handleLimitChange}
                    >
                      {[10, 20, 40, 80, 100].map((num) => (
                        <option value={num} key={num}>{num}</option>
                      ))}
                    </select>
                  </FormControl>
                </div>

                <Button onClick={downloadHandler} {...submitButtonElement2}>
                  Export To Excel
                </Button>
              </div>
            </div>

            {/* TABLE */}
            <div className="TableList overflow-auto table-responsive">
              {loader}
              <Table bordered className="table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Course</th>
                    <th>Lecture</th>
                    <th>Mobile</th>
                    <th>IP</th>
                    <th>Platform</th>
                    <th>Play Time</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {record?.length ? (
                    record.map(({
                      user,
                      IPAddress,
                      course,
                      TotalPlayedTime,
                      lecture,
                      AddedOn,
                      Platform,
                    }, i) => (
                      <tr key={i}>
                        <td>{i + 1 + (activePage - 1) * limit}</td>
                        <td>{`${user?.FirstName} ${user?.MiddleName || ""} ${user?.LastName}`}</td>
                        <td>{course?.CourseTitle}</td>
                        <td>{lecture?.LectureTitle}</td>
                        <td>{user?.Mobile}</td>
                        <td>{IPAddress}</td>
                        <td>{Platform}</td>
                        <td>{convertMinutesToHoursMinutesSeconds(TotalPlayedTime)}</td>
                        <td>{dateFormat(AddedOn)}</td>
                      </tr>
                    ))
                  ) : (
                    <NotFound cols={12} />
                  )}
                </tbody>
              </Table>
            </div>

            {total > limit && (
              <div className="Pagination">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={limit}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                />
              </div>
            )}

          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default OnDemandLectureReport;
